import { fetchDownloadFile } from "@/api/user/home";
import { CreditDataMain } from "@/types/api/user/home.type.data";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { SetStateAction, useState } from "react";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";

// ==================== Types ====================
interface CreditDetailsProps {
  userInquiry?: CreditDataMain;
  setDetail?: React.Dispatch<SetStateAction<boolean>>;
}

interface ImageConfig {
  key: string;
  src?: string;
  title: string;
}

// ==================== Styles ====================
const connectedBoxStyle = {
  height: "61px",
  display: "flex",
  alignItems: "center",
  color: "#fff",
  paddingRight: "10px",
  width: "25%",
  boxSizing: "border-box" as const,
  borderRight: "1px solid #383838",
  borderLeft: "none",
};

// ==================== Sub-Components ====================
const gradientBackground = {
  background: "linear-gradient(97.77deg, rgba(0, 0, 0, 0.4) -9.73%, rgba(23, 23, 23, 0.281106) 16.04%, rgba(115, 103, 240, 0.25) 50.23%, rgba(65, 65, 65, 0.231942) 87.66%, rgba(0, 0, 0, 0.16) 109.42%)",
};


/**
 * Renders a row with connected boxes
 */
const ConnectedRow = ({ items }: { items: string[] }) => (
    <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="space-between" sx={{border: "1px solid #383838", overflow: "hidden", width: "100%"}}>
      {items.map((text, idx) => (
          <BoxKit
              key={idx}
              sx={{...connectedBoxStyle,
                width: { xs: "100%", sm: `${100 / items.length}%` },
                borderRight: {xs: "none", sm: idx < items.length - 1 ? "1px solid #383838" : "none",},
                borderBottom: {xs: idx < items.length - 1 ? "1px solid #383838" : "none", sm: "none",},
              }}
          >
            {text}
          </BoxKit>
      ))}
    </StackKit>
);

/**
 * Renders a single full-width row
 */
const SingleFullRow = ({ content }: { content: string | React.ReactNode }) => (
    <BoxKit sx={{minHeight: "61px", display: "flex", alignItems: "center", color: "#fff", paddingRight: "10px", width: "100%", overflow: "hidden", flexWrap: "wrap",}}>
      {content}
    </BoxKit>
);

/**
 * Renders an image box with title
 */
const ImgBox = ({ src, title }: { src?: string; title: string }) => {
  if (!src) return null;

  return (
      <StackKit sx={{width: { xs: "100%", sm: "45%", md: "20%" }, height: "149px", ...gradientBackground, marginBottom: { xs: "1rem", sm: 0 },}}>
        <BoxKit sx={{width: "100%", height: "85px", overflow: "hidden", borderRadius: "8px",}}>
          <img src={src} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
        </BoxKit>
        <TypoGraphyKit variant="body1" sx={{ textAlign: "center", marginTop: "0.5rem" }}>
          {title}
        </TypoGraphyKit>
      </StackKit>
  );
};

/**
 * Renders a grid of images
 */
const ImagesGrid = ({ images }: { images: ImageConfig[] }) => {
  // Filter out images without src
  const validImages = images.filter(img => img.src);

  if (validImages.length === 0) return null;

  // Split images into rows of 3
  const CHUNK_SIZE = 3;
  const imageChunks = validImages.reduce<ImageConfig[][]>((acc, curr, index) => {
    const chunkIndex = Math.floor(index / CHUNK_SIZE);
    if (!acc[chunkIndex]) acc[chunkIndex] = [];
    acc[chunkIndex].push(curr);
    return acc;
  }, []);

  return (
      <StackKit direction="column" gap={2} sx={{ width: "100%" }}>
        {imageChunks.map((chunk, index) => (
            <StackKit key={index} direction={{ xs: "column", sm: "row" }} justifyContent={{ xs: "center", sm: "space-around" }} alignItems="center" sx={{width: "100%", flexWrap: "wrap", gap: 2,}}>
              {chunk.map(({ key, src, title }) => (
                  <ImgBox key={key} src={src} title={title} />
              ))}
            </StackKit>
        ))}
      </StackKit>
  );
};

// ==================== Helpers ====================

/**
 * Creates a download link and triggers download
 */
const downloadFile = (blob: Blob, fileName: string) => {
  const fileURL = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(fileURL);
};

/**
 * Handles different response types and triggers appropriate download
 */
const handleDownloadResponse = (response: unknown) => {
  try {
    // Handle Blob response
    if (response instanceof Blob) {
      if (response.type === 'application/pdf' || response.type.includes('pdf')) {
        downloadFile(response, 'validation_result.pdf');
        toast.success("فایل PDF با موفقیت دانلود شد");
        return;
      }

      // For text/JSON responses
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const jsonData = JSON.parse(content);
          const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
            type: 'text/plain; charset=utf-8'
          });
          downloadFile(blob, 'validation_result.json');
          toast.success("فایل با موفقیت دانلود شد");
        } catch {
          // If not JSON, save as text
          downloadFile(response, 'validation_result.txt');
          toast.success("فایل با موفقیت دانلود شد");
        }
      };
      reader.readAsText(response);
      return;
    }

    // Handle string response
    if (typeof response === 'string') {
      try {
        const jsonData = JSON.parse(response);
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
          type: 'application/json; charset=utf-8'
        });
        downloadFile(blob, 'validation_result.json');
      } catch {
        const blob = new Blob([response], { type: 'text/plain; charset=utf-8' });
        downloadFile(blob, 'validation_result.txt');
      }
      toast.success("فایل با موفقیت دانلود شد");
      return;
    }

    // Handle object response
    const blob = new Blob([JSON.stringify(response, null, 2)], {
      type: 'application/json; charset=utf-8'
    });
    downloadFile(blob, 'validation_result.json');
    toast.success("فایل با موفقیت دانلود شد");
  } catch (error) {
    toast.error("خطا در دانلود فایل");
    console.error("Download error:", error);
  }
};

// ==================== Main Component ====================

/**
 * CreditDetails Component
 *
 * Displays detailed credit information including personal info,
 * business details, and uploaded images.
 *
 * @param {CreditDetailsProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const CreditDetails: React.FC<CreditDetailsProps> = ({userInquiry, setDetail}) => {
  const [isPendingDownload, setIsPendingDownload] = useState(false);

  // ==================== Mutations ====================

  const { mutate } = useMutation({
    mutationFn: fetchDownloadFile,
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در دانلود فایل");
      setIsPendingDownload(false);
    },
    onSuccess: (response) => {
      handleDownloadResponse(response);
      setIsPendingDownload(false);
    },
  });

  // ==================== Handlers ====================

  const handleClickDownload = () => {
    setIsPendingDownload(true);
    mutate(undefined);
  };

  const handleClickReturn = () => {
    setDetail?.(false);
  };

  // ==================== Data Preparation ====================

  // Define image configurations
  const imageConfigs: ImageConfig[] = [
    {
      key: 'nid_front',
      src: userInquiry?.nid_front_image,
      title: 'روی کارت ملی'
    },
    {
      key: 'nid_back',
      src: userInquiry?.nid_back_image,
      title: 'پشت کارت ملی'
    },
    {
      key: 'birth_certificate',
      src: userInquiry?.birth_certificate_image,
      title: 'صفحه اول شناسنامه'
    },
    {
      key: 'checkbook',
      src: userInquiry?.checkbook_image,
      title: 'تصویر چک ضمانت'
    },
    {
      key: 'job',
      src: userInquiry?.job_image,
      title: 'فیش حقوقی'
    },
    {
      key: 'lease',
      src: userInquiry?.lease_image,
      title: 'تصویر جواز کسب'
    },
  ];

  // Prepare display data with fallbacks
  const personalInfo = [
    `نام: ${userInquiry?.name || "-"}`,
    `نام خانوادگی: ${userInquiry?.last_name || "-"}`,
    `شماره تلفن: ${toPersianNumber(userInquiry?.phone?.toString() || "-")}`,
    `کد ملی: ${toPersianNumber(userInquiry?.nid?.toString() || "-")}`,
  ];

  const businessInfo = [
    `شغل: ${userInquiry?.business?.business_type || "ثبت نشده"}`,
    `نام شرکت: ${userInquiry?.business?.name || "ثبت نشده"}`,
    `سمت شغلی: ${userInquiry?.business?.title || "ثبت نشده"}`,
    `شناسه ملی: ${userInquiry?.business?.company_nid || "ثبت نشده"}`,
  ];

  // ==================== Render ====================

  return (
      <Grid sx={{width: "100%", padding: { xs: "1rem", sm: "1.5rem", md: "2rem" }, overflowX: "hidden"}}>
        <StackKit gap={2}>
          {/* Header Section */}
          <Grid sx={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,}}>
            <TypoGraphyKit variant="body1">جزئیات اعتبار سنجی:</TypoGraphyKit>
            <StackKit direction="row" gap={2}>
              <CustomButton onClick={handleClickReturn}>
                بازگشت
              </CustomButton>
              <CustomButton onClick={handleClickDownload}>
                {isPendingDownload ? (
                    <StackKit sx={{ padding: "5px" }}>
                      <PulseLoader color="#fff" size={5} speedMultiplier={1} />
                    </StackKit>
                ) : (
                    "نمایش فایل"
                )}
              </CustomButton>
            </StackKit>
          </Grid>

          {/* Main Information Section */}
          <StackKit className="border border-[#383838] justify-start w-full overflow-auto" sx={gradientBackground}>
            {/* Credit Info Row */}
            <ConnectedRow items={[`مبلغ اعتبار درخواستی ${numberSeparator(userInquiry?.price)} ریال`, `مدت بازپرداخت ${toPersianNumber(userInquiry?.month?.toString() || "0")} ماهه`,]}/>

            {/* Personal Info Row */}
            <ConnectedRow items={personalInfo} />

            {/* Address Row */}
            <SingleFullRow content={`آدرس محل سکونت: ${userInquiry?.business?.address || "ثبت نشده"}`}/>

            {/* Business Info Row */}
            <ConnectedRow items={businessInfo} />

            {/* Company Address Row */}
            <SingleFullRow
                content={
                  <StackKit sx={{ padding: "20px 0", flexDirection: "column", gap: 1 }}>
                    <TypoGraphyKit textAlign="right" variant="body1">
                      آدرس شرکت: {userInquiry?.address || "ثبت نشده"}
                    </TypoGraphyKit>
                  </StackKit>
                }
            />
          </StackKit>

          {/* Images Section */}
          <StackKit alignItems="start" sx={{ width: "100%" }}>
            {/*<TypoGraphyKit variant="body1">تصاویر ارسال شده:</TypoGraphyKit>*/}
            <ImagesGrid images={imageConfigs} />
          </StackKit>
        </StackKit>
      </Grid>
  );
};

export default CreditDetails;